import React from "react";
import { Button, Input, Select, Checkbox } from "antd";
import InfoTooltip from "../UI/InfoTooltip";
import { FormDataProps } from "./insights";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useUser } from "@/contexts/UserContext";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import { getColumnTitle } from "@/utils/tableHelpers";
import { getUserRoleForTrial } from "./insights.utils";

const { TextArea } = Input;
interface CreateInsightFormProps {
  formData: FormDataProps;
  setFormData: React.Dispatch<React.SetStateAction<FormDataProps>>;
  isLoading: boolean;
  errorInsight: boolean;
  /** Short technical detail (e.g. "HTTP 400") appended to the error line. */
  errorDetail?: string;
  onSubmit: () => void;
}

export default function CreateInsightForm({
  formData,
  setFormData,
  isLoading,
  errorInsight,
  errorDetail,
  onSubmit,
}: CreateInsightFormProps) {
  const { buildUrl, id: trialId } = useScopeParams();
  const { userInfo } = useUser();
  const { isSuperuserMode } = useSuperuserMode();
  const trialRole = getUserRoleForTrial(userInfo, trialId);

  return (
    <div
      className="flex flex-col p-2 overflow-auto"
      data-testid="create-insight-panel"
    >
      {errorInsight && (
        <p className="text-xs text-red-600 font-semibold">
          Something went wrong while creating the insight
          {errorDetail ? ` (${errorDetail})` : ""}. Please try again.
        </p>
      )}
      <h2 className="text-sm text-[#2B4752] mt-3">
        Save your Ask Vivo question to your insights dashboard, and set an alert
        if needed.
      </h2>
      <h2 className="text-sm text-[#00000073] mt-1">
        View the{" "}
        <span
          className="text-[#0C98D0] underline cursor-pointer"
          onClick={() =>
            window.open(buildUrl("wiki/guides/insights-enhanced"), "_blank")
          }
        >
          Vivo Guide
        </span>{" "}
        for additional help.
      </h2>
      <div className="flex gap-2 pt-2 mt-3 mb-2">
        <label className="block text-sm  text-[#2B4752]">Insight name</label>
        <InfoTooltip
          fill="#064C68"
          title="A user-editable display name for your insight. This name does not affect the Ask Vivo response."
        />
      </div>
      <TextArea
        data-testid="create-insight-title-input"
        showCount
        value={formData.title}
        maxLength={150}
        rows={2}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, title: e.target.value }))
        }
        placeholder="Enter a name for your insight"
        style={{ resize: "none" }}
      />
      <div className="flex gap-2 pt-2 mt-4 mb-1">
        <label className="block text-sm text-[#2B4752] mb-2">
          Select where to view insight
        </label>
      </div>
      <div data-testid="create-insight-category-select">
        <Select
          value={formData.category}
          defaultValue={"uncategorized"}
          className="w-full"
          classNames={{ popup: { root: "av-select-popup" } }}
          onChange={(value: FormDataProps["category"]) =>
            setFormData((prev) => ({ ...prev, category: value }))
          }
          options={[
            { value: "uncategorized", label: "Uncategorized" },
            { value: "sites", label: "Sites" },
            { value: "subjects", label: "Subjects" },
            { value: "safety", label: "Safety" },
            { value: "clinical", label: "Clinical" },
            { value: "data", label: "Data" },
          ]}
        />
      </div>
      <div className="flex gap-2 pt-2 mt-4 mb-1">
        <label className="block text-sm text-[#2B4752] mb-2">
          Select a cadence for insight updates
        </label>
      </div>
      <div data-testid="create-insight-cadence-select">
        <Select
          value={formData.cadence}
          onChange={(value: FormDataProps["cadence"]) =>
            setFormData((prev) => ({ ...prev, cadence: value }))
          }
          className="w-full"
          classNames={{ popup: { root: "av-select-popup" } }}
          defaultValue={"daily"}
          options={[
            { label: "Daily", value: "daily" },
            { label: "Weekly", value: "weekly" },
            { label: "Monthly", value: "monthly" },
          ]}
        />
      </div>
      {isSuperuserMode && (
        <div className="flex items-center gap-2 mt-4">
          <Checkbox
            id="share-insight-checkbox"
            data-testid="create-insight-share-checkbox"
            checked={formData.is_shared || false}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, is_shared: e.target.checked }))
            }
          />
          <label
            htmlFor="share-insight-checkbox"
            className="text-sm text-[#2B4752]"
          >
            Share with{" "}
            <span className="font-bold">
              {trialRole ? getColumnTitle(trialRole) : "users"}
            </span>{" "}
            role users.
          </label>
        </div>
      )}

      <div className="flex flex-start mt-6">
        <Button
          data-testid="create-insight-submit-button"
          type="primary"
          loading={isLoading}
          onClick={onSubmit}
          disabled={!formData.title.trim()}
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
